import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { errorText } from '../../lib/api';
import { characterName, useRunScope } from '../../lib/characters';
import {
  QUICK_ACTIONS,
  defaultKnobs,
  describe,
  runQuickAction,
  type QuickActionId,
  type QuickContext,
  type QuickKnobs,
  type QuickResult,
} from './quickActions';

const btn =
  'rounded border-2 border-ink px-3 py-1 text-sm font-semibold hover:bg-caption/40 disabled:opacity-40';
const input = 'w-20 rounded border-2 border-ink bg-white px-2 py-0.5 text-sm';

/**
 * The confirm dialog of one quick action: what will happen to this player (from `describe`), the action's knobs, a
 * required reason, then the server's result (summary, details, the delivered lines with their weights) or its error.
 */
export function QuickActionDialog({
  playerId,
  action,
  context,
  onClose,
}: {
  playerId: string;
  action: QuickActionId;
  context: QuickContext;
  onClose: () => void;
}) {
  const { client, runId, character } = useRunScope();
  const qc = useQueryClient();
  const meta = QUICK_ACTIONS.find((a) => a.id === action)!;
  const [knobs, setKnobs] = useState<QuickKnobs>(() => defaultKnobs(context));
  const [reason, setReason] = useState('');
  const run = useMutation({
    mutationFn: () => runQuickAction(client, playerId, action, knobs, reason.trim(), runId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-player', playerId] });
      void qc.invalidateQueries({ queryKey: ['admin-quick-actions', playerId] });
    },
  });
  const text = describe(action, context, knobs, characterName(character));
  const set = (patch: Partial<QuickKnobs>) => setKnobs({ ...knobs, ...patch });
  const num = (v: string) => Math.max(0, Math.floor(Number(v) || 0));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={meta.label}
        className="mt-10 w-full max-w-xl rounded border-2 border-ink bg-white p-4 shadow-lg"
      >
        <h2 className="text-lg font-semibold">{meta.label}</h2>
        <p className="mb-3 text-sm text-bruise">{meta.subtitle}</p>

        {run.data ? (
          <ResultView result={run.data} />
        ) : (
          <>
            {action === 'supply-drop' && (
              <Knobs>
                <label>
                  Days{' '}
                  <input
                    aria-label="Days"
                    type="number"
                    min={1}
                    className={input}
                    value={knobs.days}
                    onChange={(e) => set({ days: num(e.target.value) })}
                  />
                </label>
                <label>
                  Magazines a day{' '}
                  <input
                    aria-label="Magazines a day"
                    type="number"
                    min={0}
                    className={input}
                    value={knobs.magazines}
                    onChange={(e) => set({ magazines: num(e.target.value) })}
                  />
                </label>
              </Knobs>
            )}
            {action === 'base-resupply' && (
              <Knobs>
                <label>
                  Multiplier{' '}
                  <input
                    aria-label="Multiplier"
                    type="number"
                    min={1}
                    className={input}
                    value={knobs.multiplier}
                    onChange={(e) => set({ multiplier: num(e.target.value) })}
                  />
                </label>
              </Knobs>
            )}
            {action === 'homebase-reset' && (
              <Knobs>
                {(
                  [
                    ['camps', 'Camps / outposts'],
                    ['home', 'Home location'],
                    ['facilities', 'Facilities / base modules'],
                    ['buildOrders', 'Build orders'],
                    ['inventory', 'Inventory at base'],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={knobs[key]}
                      onChange={(e) => set({ [key]: e.target.checked })}
                    />
                    {label}
                  </label>
                ))}
              </Knobs>
            )}

            <h3 className="text-sm font-semibold">What will happen</h3>
            {text.blocked ? (
              <p className="my-2 text-sm text-blam">{text.blocked}</p>
            ) : (
              <ul className="my-2 list-disc pl-5 text-sm">
                {text.lines.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            )}
            {text.never && <p className="mb-3 text-xs text-bruise">{text.never}</p>}

            <label className="block text-sm font-semibold">
              Reason (audited)
              <textarea
                aria-label="Reason"
                className="mt-1 block w-full rounded border-2 border-ink px-2 py-1 text-sm font-normal"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            {run.isError && <p className="mt-2 text-sm text-blam">{errorText(run.error)}</p>}
          </>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className={btn} onClick={onClose}>
            {run.data ? 'Close' : 'Cancel'}
          </button>
          {!run.data && (
            <button
              type="button"
              className={`${btn} bg-caption`}
              disabled={!reason.trim() || !!text.blocked || run.isPending}
              onClick={() => run.mutate()}
            >
              {run.isPending ? 'Working…' : `Confirm ${meta.label.toLowerCase()}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Knobs({ children }: { children: ReactNode }) {
  return (
    <div className="mb-3 flex flex-wrap gap-3 rounded bg-newsprint p-2 text-sm">{children}</div>
  );
}

function ResultView({ result }: { result: QuickResult }) {
  return (
    <div aria-label="Result">
      <p className={`font-semibold ${result.changed ? '' : 'text-bruise'}`}>{result.summary}</p>
      {result.details.length > 0 && (
        <ul className="my-2 list-disc pl-5 text-sm">
          {result.details.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      )}
      {result.items.length > 0 && (
        <table className="mt-2 w-full text-left text-xs">
          <thead>
            <tr>
              <th>Item</th>
              <th>Qty</th>
              <th>kg</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {result.items.map((i) => (
              <tr key={i.itemId} className="border-t border-ink/20">
                <td>{i.name}</td>
                <td>{i.quantity}</td>
                <td>{i.kg.toFixed(1)}</td>
                <td>{i.why}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-ink font-semibold">
              <td>Total</td>
              <td>{result.items.reduce((n, i) => n + i.quantity, 0)}</td>
              <td>{result.totalKg.toFixed(1)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
